import { ChangeDetectionStrategy, Component, HostBinding, HostListener, inject, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { EVENT_MESSAGES, MESSAGE_RECIPIENTS } from '@config';
import { filter, map, mergeMap, takeUntil, tap } from 'rxjs/operators';

import { MessageBus } from '@23degrees/shared/bus/message/message.bus';
import { BaseClass } from '@23degrees/shared/classes/base.class';
import { detectTouch } from '@23degrees/shared/functions/detectTouch';
import { setViewPortHeight } from '@23degrees/shared/functions/setViewportHeight';
import { SwUpdate } from '@angular/service-worker';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet],
})
export class AppComponent extends BaseClass {
  @HostBinding('class.native-scrolling') nativeScroll: boolean = true;

  // nativeScrolling$: Signal<boolean> = this.uiStore.nativeScrolling$;
  swUpdate: SwUpdate = inject(SwUpdate);

  @HostListener('window:resize', ['$event'])
  updateSize(event) {
    setViewPortHeight(this.globalService.document);
  }

  /**
   * Global host listener - used for inside / outside checks
   */
  @HostListener('document:click', ['$event', '$event.target'])
  onClick(event: MouseEvent, targetElement: HTMLElement): void {
    this.messageBus.emit(EVENT_MESSAGES.Click, [MESSAGE_RECIPIENTS.All], targetElement);
  }

  private router: Router = inject(Router);
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private messageBus: MessageBus = inject(MessageBus);

  // private uiStore: UiStore,
  // private authQuery: AuthQuery,
  // // private contentManager: ContentManager // Needs to be here -> TODO: refactor it out // > providedIn fixed this and otherwise provideContentManger is to use

  //please do not remove unused managers
  //need them for instancing here!
  constructor() {
    super();

    if (this.globalService.isBrowser()) {
      this.registerServiceWorkerListerners();

      // setTimeout(() => {
      //   this.checkForNewVersion();
      // }, 4000);

      this.router.events.subscribe((event) => {
        if (event instanceof NavigationStart) {
          // console.log('>> NavStart:', event.url);
          // console.trace();
        }

        if (event instanceof NavigationEnd) {
          // console.log('>> NavEnd:', event.url);
          // console.trace();
        }
      });
    }
  }

  ngOnInit() {
    this.router.events
      .pipe(
        takeUntil(this.componentDestroyed$),
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) route = route.firstChild;
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data),
        filter((event) => event.title),
      )
      .subscribe((event) => this.globalService.setMetaTags(event.title));

    // this.nativeScrolling$.pipe(takeUntil(this.componentDestroyed$)).subscribe((nativeScroll) => (this.nativeScroll = nativeScroll));
    const body = this.globalService.document.body as HTMLElement;
    detectTouch() ? body.classList.add('touch-device') : body.classList.remove('touch-device');
    setViewPortHeight(this.globalService.document);
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  registerServiceWorkerListerners() {
    this.swUpdate.versionUpdates
      .pipe(
        takeUntil(this.componentDestroyed$),
        tap((update) => {
          if (update.type === 'VERSION_DETECTED') {
            // activateUpdate() will trigger the 'VERSION_READY' or 'VERSION_INSTALLATION_FAILED' event when done
            console.log('[ServiceWorker] - New server version detected, trying to install...', update);
            // this.swUpdate.activateUpdate().then();
          }

          if (update.type === 'VERSION_READY') {
            // this._reloadPage will be set to true, asking a full page reload on next navigation
            console.log('[ServiceWorker] - New server version installed');
          }

          if (update.type === 'VERSION_INSTALLATION_FAILED') {
            // this._clearCacheAndReload will be set to true, asking a cache clear and full page reload on next navigation
            console.warn('[ServiceWorker] - Error while installing update, cache will be cleared and page reloaded', update);
            // this.clearCacheStorage().then((clearCacheStorage) => { window.location.reload(); })
          }

          if (update.type === 'NO_NEW_VERSION_DETECTED') {
            // this._clearCacheAndReload will be set to true, asking a cache clear and full page reload on next navigation
            console.log('[ServiceWorker] - No new version detected');
          }
        }),
      )
      .subscribe();
  }

  // https://codebeyondlimits.com/articles/service-workers-in-angular-the-complete-setup-guide-with-best-practices-and-troubleshooting-tips
  checkForNewVersion = async () => {
    try {
      // Check if Service Worker is supported by the Browser
      if (this.swUpdate.isEnabled) {
        console.log('[ServiceWorker] - is enabled');

        // Check if new version is available
        const isNewVersion = await this.swUpdate.checkForUpdate();
        console.log('[ServiceWorker] - is new version', isNewVersion);
        if (isNewVersion) {
          // Check if new version is activated
          const isNewVersionActivated = await this.swUpdate.activateUpdate();

          // Reload the application with new version if new version is activated
          if (isNewVersionActivated) console.log('NEW VERSION ACTIVATED');
          //  window.location.reload();
        }
      }
    } catch (error) {
      console.log('[ServiceWorker] - Error when checking for new version of the application: ', error);
      // window.location.reload();
    }
  };

  clearCacheStorage(): Promise<Promise<boolean>[][]> {
    return caches.keys().then((cacheKeys) =>
      Promise.all(
        cacheKeys.map((cacheKey) => {
          const ngswRegex = /^(ngsw).*/;
          if (ngswRegex.test(cacheKey)) {
            return caches.open(cacheKey).then((cache) => cache.keys().then((requests) => requests.map((req) => cache.delete(req))));
          }
        }),
      ),
    );
  }
}
